import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TRANSFER_OUTSIDE_FEE, type Lang } from "@majourneys/shared";
import { convertFromMad, currencyLabelFor, useCurrency } from "../lib/currency";
import { formatNumber } from "../lib/format";
import { PW, pewterInset } from "../internal-ui/pewter-shell";
import { transferVehicle } from "../lib/transfer-vehicle";

/**
 * Formats a dirham amount in the guest's display currency, like every other
 * price on the site: the driver takes dollars, euros, pounds or dirhams, so
 * the quote is shown in whichever the guest picked (owner, 2026-09-23).
 *
 * Same context, rates and rounding as `useDisplayPrice` (whole numbers in
 * every currency, never decimals: owner, 2026-09-28), with one difference: the digits are pinned
 * to Latin with `-u-nu-latn` in every locale, ar included, rather than left to
 * the locale's default numbering system.
 */
export function useTransferMoney(lang: Lang): (madValue: number) => string {
  const { currency, rates } = useCurrency();
  return useCallback(
    (madValue: number) => {
      const converted = convertFromMad(madValue, currency, rates);
      const rounded = Math.round(converted);
      return `${formatNumber(rounded, `${lang}-u-nu-latn`, { maximumFractionDigits: 0 })} ${currencyLabelFor(currency, lang)}`;
    },
    [currency, rates, lang],
  );
}

/**
 * A sum of dirham amounts as shown: each converted and rounded first, then
 * added, so the total row equals the rows above it (owner, 2026-09-29).
 */
export function useTransferSum(lang: Lang): (...madValues: number[]) => string {
  const { currency, rates } = useCurrency();
  return useCallback(
    (...madValues: number[]) => {
      const sum = madValues.reduce(
        (acc, mad) => acc + (mad > 0 ? Math.round(convertFromMad(mad, currency, rates)) : 0),
        0,
      );
      return `${formatNumber(sum, `${lang}-u-nu-latn`, { maximumFractionDigits: 0 })} ${currencyLabelFor(currency, lang)}`;
    },
    [currency, rates, lang],
  );
}

export interface TransferPriceSummaryProps {
  /** The most people the chosen vehicle seats; picks its icon and name. */
  maxGuests: number;
  /** Dirhams; converted for display only. */
  price: number;
  /** The outside-Marrakech fee in dirhams; the line is hidden when it is 0. */
  fee: number;
  /** A typed place (no pin) may still be outside: say the fee could apply. */
  typedFeeNote?: boolean;
  lang: Lang;
}

/**
 * The vehicle the form picked for the party: its picture, its name and the
 * most it seats. Shown in the form's "Who" step, right under the people count
 * that chose it.
 */
export function TransferVehicleLine({ maxGuests }: { maxGuests: number }) {
  const { t } = useTranslation();
  const vehicle = transferVehicle(maxGuests);
  return (
    <div className="flex min-w-0 items-center gap-3" data-testid="transfer-vehicle">
      {/* Decorative: the vehicle's name sits right next to it. */}
      <img
        src={vehicle.icon}
        alt=""
        width={56}
        height={40}
        decoding="async"
        className="h-10 w-14 shrink-0 object-contain"
      />
      <span className="min-w-0 text-start">
        <span className="block font-medium">{t(vehicle.nameKey)}</span>
        <span className="block text-[13px]" style={{ color: PW.muted }}>
          {t("transport.up_to_people", { count: maxGuests })}
        </span>
      </span>
    </div>
  );
}

/** The price rows at the end of the transfer form: vehicle, the fee when due, the total. */
export function TransferPriceSummary({ maxGuests, price, fee, typedFeeNote, lang }: TransferPriceSummaryProps) {
  const { t } = useTranslation();
  const money = useTransferMoney(lang);
  const sum = useTransferSum(lang);
  const vehicle = transferVehicle(maxGuests);
  return (
    <div className="rounded-xl p-3" style={{ ...pewterInset, color: PW.navy }}>
      <div className="flex items-center justify-between gap-3" data-testid="transfer-vehicle-price">
        <span className="min-w-0 text-start">{t(vehicle.nameKey)}</span>
        <span className="whitespace-nowrap">{money(price)}</span>
      </div>
      {fee > 0 && (
        <div className="mt-1 flex justify-between gap-3" style={{ color: PW.goldDark }} data-testid="transfer-fee">
          <span>{t("transport.outside_fee")}</span>
          <span className="whitespace-nowrap">+ {money(fee)}</span>
        </div>
      )}
      <div
        className="mt-2 flex justify-between gap-3 pt-2 font-semibold"
        style={{ borderTop: `1px solid ${PW.hairline}` }}
        data-testid="transfer-total"
        aria-live="polite"
      >
        <span>{t("transport.total_on_arrival")}</span>
        <span className="whitespace-nowrap">{sum(price, fee)}</span>
      </div>
      {typedFeeNote && (
        <p className="mt-2 text-[12px]" style={{ color: PW.muted }} data-testid="transfer-typed-fee-note">
          {t("transport.typed_fee_note", { fee: money(TRANSFER_OUTSIDE_FEE) })}
        </p>
      )}
    </div>
  );
}
